// Per-object brand-mark decals.
//
// Each placed object can show the OneQode iconmark or wordmark on one face of
// the user's choosing (front / back / left / right / top / none). Decals reuse
// the already-baked IconMark_Master / Logo_Master GLBs. Their geometry comes
// from Blender; runtime materials default to white for contrast and can be
// switched independently to neutral or brand blue.
//
// The decal mesh is parented to the host object as a named child
// `oq-brandmark`, so toggling re-runs are O(1): we remove the old child and
// create a new one. Host transforms (position, rotation, scale) carry the
// decal automatically.

import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { getMaster } from "./assetRegistry";
import { createBrandMaterial, DEFAULT_ACCENT_HEX } from "./accent";
import type { BrandMaterialMode } from "./types";

export type BrandMarkKind = "icon" | "logo";
export type BrandSide = "front" | "back" | "left" | "right" | "top";

const CHILD_NAME = "oq-brandmark";
const SURFACE_EPSILON = 0.005;

/** Outward normal in object-local space for each side.
 *
 * Side labels follow the user's view-of-the-object mental model rather than
 * raw three.js coordinate signs:
 *   • "front"   → the -Z face (the side a viewer in front of the object
 *                  looks at; default cameras tend to sit on this side, so
 *                  the +Z face would be facing AWAY and read mirrored).
 *   • "back"    → the +Z face.
 *   • "left"    → the -X face (left of the object as it stands).
 *   • "right"   → the +X face.
 *   • "top"     → the +Y face.
 *
 * The loaded GLB masters are browser-space Y-up geometry:
 *   • +X = mark width / text direction
 *   • +Y = mark height / visual up
 *   • -Z = visible face normal
 *   • +Z = back face / into the host
 *
 * The placement basis below maps -Z to this outward normal, which keeps the
 * mark flat against the target face instead of standing upright on top.
 */
function sideNormal(side: BrandSide): THREE.Vector3 {
  switch (side) {
    case "front": return new THREE.Vector3(0, 0, -1);
    case "back":  return new THREE.Vector3(0, 0, 1);
    case "left":  return new THREE.Vector3(-1, 0, 0);
    case "right": return new THREE.Vector3(1, 0, 0);
    case "top":   return new THREE.Vector3(0, 1, 0);
  }
}

/** Build a host-local orientation for a browser-space brand mark.
 *
 * Matrix columns are the desired host-local directions for source +X, +Y,
 * and +Z. Since the source's visible face is -Z, source +Z points inward
 * after placement. For vertical walls we keep source +Y aligned to world up
 * and derive the outside-viewer's left-to-right axis from the face normal.
 */
function basisForSide(
  side: BrandSide,
  n: THREE.Vector3,
  flipped: boolean,
  inverted: boolean,
): THREE.Matrix4 {
  const zDir = n.clone().multiplyScalar(-1).normalize();
  let xDir: THREE.Vector3;
  let yDir: THREE.Vector3;

  if (side === "top") {
    xDir = new THREE.Vector3(1, 0, 0);
    yDir = new THREE.Vector3(0, 0, 1);
  } else {
    yDir = new THREE.Vector3(0, 1, 0);
    xDir = yDir.clone().cross(zDir).normalize();
  }

  if (flipped) {
    // 180° rotation in-plane: flip both axes.
    xDir.multiplyScalar(-1);
    yDir.multiplyScalar(-1);
  }
  if (inverted) {
    // 180° rotation around the face's vertical axis (Y). Flips X (left-
    // right) AND Z (depth) — net effect is the icon spins to face the
    // user from the other side, correcting cases where the side's
    // natural basis shows the glyph reversed. The single-axis mirror
    // I tried first read as a 90° tilt at iso angle; this version is
    // a true half-turn that lands the icon flat-against the same face
    // but oriented the right way round.
    xDir.multiplyScalar(-1);
    zDir.multiplyScalar(-1);
  }

  return new THREE.Matrix4().makeBasis(xDir, yDir, zDir);
}

/**
 * Detach any prior brand-mark child from `host`, disposing its geometry.
 */
export function clearBrandMark(host: THREE.Object3D): void {
  const prior = host.children.find((c) => c.name === CHILD_NAME);
  if (!prior) return;
  host.remove(prior);
  const materials = new Set<THREE.Material>();
  prior.traverse((c) => {
    const m = c as THREE.Mesh;
    if (!m.isMesh) return;
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    for (const mat of mats) materials.add(mat);
  });
  for (const mat of materials) mat.dispose();
}

/**
 * Attach (or replace) a brand-mark decal on `host`.
 *
 * The decal lives inside an internal group, parented to `host`, so when the
 * host moves/rotates/scales the mark follows automatically. Returns the new
 * group.
 */
export async function applyBrandMark(
  host: THREE.Object3D,
  kind: BrandMarkKind,
  side: BrandSide,
  /** Thickness multiplier (1.0 = master's authored 0.32m extrusion). The
   *  default 0.15 gives ~5cm proud — a sticker-thin look that reads
   *  correctly when laid flat on a face, matching the reference render. */
  thickness: number = 0.15,
  flipped: boolean = false,
  inverted: boolean = false,
  materialMode: BrandMaterialMode = "white",
  accentHex: number = DEFAULT_ACCENT_HEX,
): Promise<THREE.Object3D | null> {
  clearBrandMark(host);

  // Build the decal source. IconMark_Master.glb / Logo_Master.glb already
  // exist in the bake. Clone so transforms are independent.
  const masterName = kind === "icon" ? "IconMark_Master" : "Logo_Master";
  let src: THREE.Object3D;
  try {
    const master = await getMaster(masterName);
    src = SkeletonUtils.clone(master);
  } catch (e) {
    console.warn(`[brandMark] failed to load ${masterName}:`, e);
    return null;
  }

  // Compute the host's bbox in HOST-LOCAL space. `setFromObject` only gives
  // us a world-space AABB; transforming just the min/max corners back via
  // the inverse matrix is wrong as soon as the host has any rotation
  // (and even at rotation=0, scale!=1 messes up the result for shallow
  // axes). Walk the host's geometry directly and union the corners of each
  // mesh's geometry-bbox transformed into host-local space.
  host.updateWorldMatrix(true, true);
  const invHost = host.matrixWorld.clone().invert();
  const localBox = new THREE.Box3();
  const tmpCorner = new THREE.Vector3();
  host.traverse((c) => {
    if (c.userData?.brandMark) return; // skip the prior decal wrap if any
    const m = c as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
    const gbox = m.geometry.boundingBox;
    if (!gbox) return;
    m.updateWorldMatrix(true, false);
    // Compose: host-local = invHost × meshWorld
    const meshLocal = invHost.clone().multiply(m.matrixWorld);
    // 8 corners of the geometry bbox -> host-local space -> expand box
    for (let xi = 0; xi < 2; xi++) {
      for (let yi = 0; yi < 2; yi++) {
        for (let zi = 0; zi < 2; zi++) {
          tmpCorner.set(
            xi ? gbox.max.x : gbox.min.x,
            yi ? gbox.max.y : gbox.min.y,
            zi ? gbox.max.z : gbox.min.z,
          );
          tmpCorner.applyMatrix4(meshLocal);
          localBox.expandByPoint(tmpCorner);
        }
      }
    }
  });
  if (localBox.isEmpty()) {
    // Defensive fallback: a 1m cube centered at origin.
    localBox.set(new THREE.Vector3(-0.5, 0, -0.5), new THREE.Vector3(0.5, 1, 0.5));
  }
  const center = new THREE.Vector3();
  localBox.getCenter(center);
  const size = new THREE.Vector3();
  localBox.getSize(size);

  // Face center + outward normal in local space. The face-center coordinate
  // along the side's axis snaps to the bbox extent matching the outward
  // normal (front: -Z extent → min.z; back: +Z → max.z; etc.).
  const n = sideNormal(side);
  const faceCenter = center.clone();
  if (side === "front")      faceCenter.z = localBox.min.z;
  else if (side === "back")  faceCenter.z = localBox.max.z;
  else if (side === "right") faceCenter.x = localBox.max.x;
  else if (side === "left")  faceCenter.x = localBox.min.x;
  else if (side === "top")   faceCenter.y = localBox.max.y;

  // Choose a uniform decal scale: ~30% of the face's shorter dimension.
  let shorter: number;
  if (side === "front" || side === "back") shorter = Math.min(size.x, size.y);
  else if (side === "left" || side === "right") shorter = Math.min(size.z, size.y);
  else shorter = Math.min(size.x, size.z);
  shorter = Math.max(0.001, shorter);

  const srcBox = new THREE.Box3().setFromObject(src);
  const srcSize = new THREE.Vector3();
  srcBox.getSize(srcSize);

  // Icon decals at 30% of the face's shorter dim feel right; logo (the
  // wordmark) is much wider, so we let it take 60% so it actually reads as a
  // brand label rather than a tiny stamp. The source longest dimension comes
  // from the loaded GLB so the code stays correct if the masters are rebaked.
  const masterLongest = Math.max(srcSize.x, srcSize.y, 1e-4);
  const targetFraction = kind === "icon" ? 0.30 : 0.60;
  const targetLongest = shorter * targetFraction;
  const decalScale = Math.max(1e-4, targetLongest / masterLongest);

  // Wrap in a group so we can set position/rotation/scale cleanly without
  // fighting the GLB's internal transform.
  const wrap = new THREE.Group();
  wrap.name = CHILD_NAME;
  wrap.userData.brandMark = true;

  // Recenter the mark in its visual plane (X/Y), then put the back face
  // (+Z) at local z=0. The geometry then extends along -Z, and the placement
  // basis maps that -Z direction to the chosen face normal.
  const srcCenter = new THREE.Vector3();
  srcBox.getCenter(srcCenter);
  src.position.set(-srcCenter.x, -srcCenter.y, -srcBox.max.z);
  if (kind === "logo") {
    // The SVG-derived wordmark is mirrored after GLB axis conversion. Flip it
    // once in source space so it reads correctly from the outside on every
    // target face.
    src.scale.x *= -1;
  }

  // Rotate the wrap so the mark's visible -Z face lies flat on the host face.
  wrap.quaternion.setFromRotationMatrix(basisForSide(side, n, flipped, inverted));
  wrap.add(src);
  const decalMat = createBrandMaterial(materialMode, accentHex);
  decalMat.side = THREE.DoubleSide;
  src.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.material = decalMat;
  });

  // Step 3: position + scale.
  // • Position the wrap just proud of the chosen face so the back face reads
  //   as touching the host without z-fighting.
  // • Non-uniform scale: face-plane axes (X/Y in source frame) share the
  //   fit-to-face decalScale; the thickness axis (Z) takes the user's
  //   thickness multiplier.
  wrap.position.copy(faceCenter).addScaledVector(n, SURFACE_EPSILON);
  const t = Math.max(0.05, Math.min(5.0, thickness));
  wrap.scale.set(decalScale, decalScale, decalScale * t);

  host.add(wrap);
  return wrap;
}
